'use client';

import {BlockList, BlockListItem} from '@udemy-v2/react-core-components';
import {Icon} from '@udemy-v2/icons';
import {Duration, useI18n} from '@udemy-v2/i18n';
import {AccordionPanel, AccordionPanelControlled} from '@udemy-v2/react-reveal-components';
import classNames from 'classnames';
import {useState} from 'react';

import styles from './curriculum-section.module.scss';
import {CurriculumLecture, Lecture} from './lecture.react-component';
import {CourseSection} from '@udemy-v2/browse-course';
import {curriculumItemIcon} from './curriculumItemIcon';
import {useClpCourse} from '@/features/course-landing-page/context/clp-course-provider.react-component';

interface SectionProps {
    arrayIndex: number;
    title: string;
    items: CourseSection['items'];
    numLectures?: number;
    contentLength?: number;
    areAllSectionsExpanded?: boolean;
    showSectionContentsText?: boolean;
}

export const CurriculumSection = ({
    arrayIndex,
    title,
    items,
    numLectures,
    contentLength = 300, // Hardcoded default value
    areAllSectionsExpanded,
    showSectionContentsText = true,
}: SectionProps) => {
    const {ninterpolate} = useI18n();
    const course = useClpCourse();
    const {isPracticeTestCourse} = course;

    // Track user's local toggle state along with the areAllSectionsExpanded value it was set for
    const [localState, setLocalState] = useState<{
        expanded: boolean | null;
        forValue: boolean | undefined;
    }>({
        expanded: null,
        forValue: areAllSectionsExpanded,
    });

    // If areAllSectionsExpanded changed, reset local state during render
    // This allows the parent's expand/collapse all to override user toggles
    let localExpanded = localState.expanded;
    if (localState.forValue !== areAllSectionsExpanded) {
        localExpanded = null;
        // Update state during render - React will re-render immediately
        setLocalState({expanded: null, forValue: areAllSectionsExpanded});
    }

    // Compute the effective expanded state:
    // 1. If user has toggled this section locally, use that
    // 2. Otherwise, if areAllSectionsExpanded is set, use that
    // 3. Otherwise, default to first section expanded
    const getDefaultExpanded = () => (arrayIndex === 0 ? true : false);

    const expanded =
        localExpanded !== null
            ? localExpanded
            : typeof areAllSectionsExpanded === 'boolean'
              ? areAllSectionsExpanded
              : getDefaultExpanded();

    const handleToggle = (isExpanded: boolean) => {
        setLocalState({expanded: isExpanded, forValue: areAllSectionsExpanded});
    };

    function renderTitle({
        children,
        className,
        ...props
    }: {
        children: React.ReactNode;
        className?: string;
    }) {
        return (
            <h3 {...props} className={classNames(className, styles['section-title-container'])}>
                {children}
            </h3>
        );
    }

    function constructPanelDisplay() {
        return (
            <>
                <span className={styles['section-title']}>{title}</span>
                {!isPracticeTestCourse && showSectionContentsText && numLectures && (
                    <span
                        className={classNames(
                            'ud-text-sm',
                            styles['hidden-on-mobile'],
                            styles['section-content-stats'],
                        )}
                        data-purpose="section-content-stats"
                    >
                        {ninterpolate(
                            '%(numLectures)s lecture',
                            '%(numLectures)s lectures',
                            numLectures,
                            {numLectures},
                        )}
                        {contentLength && (
                            <>
                                {' • '}
                                <Duration numSeconds={contentLength} presentationStyle="human" />
                            </>
                        )}
                    </span>
                )}
            </>
        );
    }

    if (isPracticeTestCourse) {
        return (
            <AccordionPanel
                title={constructPanelDisplay()}
                defaultExpanded={arrayIndex === 0}
                className={styles.panel}
            >
                <BlockList iconAlignment="left" size="small">
                    {items.map((item, index) => {
                        const icon = (
                            <Icon
                                color="neutral"
                                glyph={curriculumItemIcon(item.type)}
                                label={false}
                            />
                        );
                        return (
                            <BlockListItem
                                key={item.id + index}
                                icon={icon}
                                className={styles['section-content']}
                            >
                                <span
                                    className={styles['item-title']}
                                    data-purpose="practice-test-title"
                                >
                                    {item.title}
                                </span>
                                <span className={styles['hidden-on-mobile']} style={{flex: 1}} />
                            </BlockListItem>
                        );
                    })}
                </BlockList>
            </AccordionPanel>
        );
    }

    return (
        <AccordionPanelControlled
            title={constructPanelDisplay()}
            renderTitle={renderTitle}
            className={styles.panel}
            expanded={expanded}
            onToggle={handleToggle}
        >
            <BlockList iconAlignment="left" size="small">
                {items.map((item, index) => (
                    <Lecture
                        key={index}
                        lecture={item as CurriculumLecture}
                        showSectionContentsText={showSectionContentsText}
                    />
                ))}
            </BlockList>
        </AccordionPanelControlled>
    );
};
